<template>
  <VaNavbar class="app-layout-navbar py-2 px-0">
    <template #left>
      <div class="left">
        <Transition v-if="isMobile" name="icon-fade" mode="out-in">
          <VaIcon
            color="primary"
            :name="isSidebarMinimized ? 'menu' : 'close'"
            size="24px"
            style="margin-top: 3px"
            @click="isSidebarMinimized = !isSidebarMinimized"
          />
        </Transition>
        <RouterLink :to="{ name: landingRouteName }" aria-label="Visit home page">
          <VuesticLogo />
        </RouterLink>
      </div>
    </template>
    <template #center>
      <div class="center">
        <div class="business-info">
          <div class="min-w-0 flex-1">
            <div class="flex items-center gap-2 min-w-0">
              <span class="business-info__label">Business</span>
              <span class="business-info__dot" />
              <span class="text-sm font-extrabold text-green-700 truncate" :title="activeBusinessName">
                {{ activeBusinessName }}
              </span>
            </div>

            <div v-if="activeBranchName" class="flex items-center gap-2 min-w-0">
              <span class="business-info__label">Branch</span>
              <span class="business-info__dot" />
              <span class="text-sm font-semibold text-gray-900 truncate" :title="activeBranchName">
                {{ activeBranchName }}
              </span>
            </div>

            <div v-if="activeLocationName" class="flex items-center gap-2 min-w-0">
              <span class="business-info__label">Location</span>
              <span class="business-info__dot" />
              <span class="text-sm font-semibold text-gray-900 truncate" :title="activeLocationName">
                {{ activeLocationName }}
              </span>
            </div>
          </div>

          <VaButton class="shrink-0" preset="secondary" size="small" @click="onSwitchBusiness">
            <VaIcon name="mso-sync" class="mr-1" />
            Switch
          </VaButton>
        </div>
      </div>
    </template>
    <template #right>
      <AppNavbarActions class="app-navbar__actions" :is-mobile="isMobile" />
    </template>
  </VaNavbar>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useModal } from 'vuestic-ui'
import { useGlobalStore } from '../../stores/global-store'
import { useUserStore } from '../../store/user'
import { authorizedLandingRouteName } from '../../utils/authorizedLanding'
import AppNavbarActions from './components/AppNavbarActions.vue'
import VuesticLogo from '../VuesticLogo.vue'
import { useBusinessStore } from '../../store/business'

defineProps({
  isMobile: { type: Boolean, default: false },
})

const GlobalStore = useGlobalStore()
const businessStore = useBusinessStore()
const userStore = useUserStore()
const { confirm } = useModal()

const { isSidebarMinimized } = storeToRefs(GlobalStore)

const landingRouteName = computed(() => authorizedLandingRouteName())
const activeBusinessName = computed(() => userStore.authUser?.active_business?.name ?? '—')
const activeBranchName = computed(() => userStore.authUser?.active_branch_name ?? '')
const activeLocationName = computed(() => userStore.authUser?.active_inventory_location?.name ?? '')

const onSwitchBusiness = async () => {
  const confirmed = await confirm({
    title: 'Switch Business',
    message: 'Are you sure you want to switch to another business?',
    okText: 'Yes, Switch',
    cancelText: 'No, Stay Here',
    size: 'small',
    maxWidth: '300px',
  })
  if (confirmed) {
    businessStore.dispatchUnSetActiveBusiness()
  }
}
</script>

<style lang="scss" scoped>
.app-layout-navbar {
  z-index: 2;
  grid-template: 'left center right' / minmax(0, 1fr) minmax(0, 32rem) minmax(0, 1fr);
}

:deep(.va-navbar__center) {
  min-width: 0;
  max-width: 100%;
}

.center {
  min-width: 0;
  width: 100%;
}

.business-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
  width: 100%;
  max-width: 32rem;
  border-radius: 0.75rem;
  border: 1px solid rgb(220 252 231);
  background-color: rgb(255 255 255 / 90%);
  padding: 0.375rem 0.75rem;
  box-shadow: 0 1px 2px rgb(0 0 0 / 5%);
  backdrop-filter: blur(8px);
}

.business-info__label {
  flex-shrink: 0;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.025em;
  text-transform: uppercase;
  color: rgb(107 114 128);
}

.business-info__dot {
  flex-shrink: 0;
  height: 0.25rem;
  width: 0.25rem;
  border-radius: 9999px;
  background-color: rgb(209 213 219);
}

.left {
  display: flex;
  align-items: center;
  margin-left: 1rem;

  & > * {
    margin-right: 1rem;
  }

  & > *:last-child {
    margin-right: 0;
  }
}

@media screen and (max-width: 950px) {
  .app-layout-navbar {
    grid-template:
      'left right' auto
      'center center' auto / minmax(0, 1fr) auto;
    row-gap: 0.375rem;
  }

  :deep(.va-navbar__center) {
    width: 100%;
    justify-content: stretch;
    padding: 0 1rem 0.125rem;
  }

  .business-info {
    max-width: none;
  }

  .business-info__label,
  .business-info__dot {
    display: none;
  }

  .app-navbar__actions {
    display: flex;
    justify-content: space-between;
  }
}

.icon-fade-enter-active,
.icon-fade-leave-active {
  transition: transform 0.5s ease;
}

.icon-fade-enter,
.icon-fade-leave-to {
  transform: scale(0.5);
}
</style>
